import React, { useEffect, useState, useContext } from "react";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  Row,
  Col,
} from "reactstrap";
import FalconCardHeader from "../common/FalconCardHeader";
import WizardError from "../auth/register/WizardError";
import classNames from "classnames";
import ButtonIcon from "../common/ButtonIcon";
import Datetime from "react-datetime";
import { useForm } from "react-hook-form";
import NumberFormat from "react-number-format";
import { faUndo, faSave } from "@fortawesome/free-solid-svg-icons";
import withRedirect from "../../helpers/hoc/withRedirect";
import { useParams, useLocation } from "react-router";
import { PreloadState } from "../UI/preloading/PreloadState";
import { useHistory } from "react-router-dom";
import { fetchAccountingAreas } from "../../helpers/api/accountingAreas";
import { fetchProviders } from "../../helpers/api/provider";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { formatAmountForBackend } from "../../helpers/currencyConversion";
import moment from "moment";
import { CurrencyContext } from "../../context/Context";
import useModuleBase from "../../helpers/hooks/useModuleBase";
import { Toast } from "../UI/toast/Toast";
import ExpensePaymentFields, { ExpensePaidNotice, emptyExpensePayment, expensePaymentError, expensePaymentPayload } from "./ExpensePaymentFields";
import { splitByWeight } from "../../helpers/splitByWeight";

// Edición de un gasto particular (type_id=3) ya existente. A diferencia del
// formulario de creación, acá no se vuelve a elegir la lista de propietarios
// ni el método de distribución (equitativo/fijo/manual/alícuota especial):
// se editan directamente los montos ya asignados a cada propiedad, que es lo
// único que el backend necesita para recalcular la asignación pendiente
// (ver FinanceController::update).
const CreateIndividualCostsEditForm = ({ common, handleSubmitData, onCancel }) => {
  const moduleBase = useModuleBase();
  const { id } = useParams();
  const { state } = useLocation();
  const history = useHistory();
  const { baseCurrency } = useContext(CurrencyContext);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
  });

  const [accountingAreas, setAccountingAreas] = useState([]);
  const [providers, setProviders] = useState([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [amountsByProperty, setAmountsByProperty] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [payment, setPayment] = useState(emptyExpensePayment);
  const [totalDraft, setTotalDraft] = useState(null);
  const amountLocked = Boolean(common?.next_charge?.amount_locked);
  const hasSeveralOwners = (common?.properties || []).length > 1;

  const expenseAccountingAreas = accountingAreas.filter((area) => {
    const code = area.account_plan?.code || area.accountPlan?.code;
    return code && code.startsWith("6");
  });

  const handleDateChange = (d) => {
    if (moment.isMoment(d) && d.isValid()) {
      const formattedDate = d.format("YYYY-MM-DD HH:mm:ss");
      setSelectedDate(d);
      setValue("expired_at", formattedDate, { shouldValidate: true, shouldDirty: true });
    } else if (typeof d === "string" && d.trim() !== "") {
      const parsed = moment(d, "DD/MM/YYYY");
      if (parsed.isValid()) {
        setSelectedDate(parsed);
        setValue("expired_at", parsed.format("YYYY-MM-DD HH:mm:ss"), { shouldValidate: true, shouldDirty: true });
      }
    } else {
      setSelectedDate(null);
      setValue("expired_at", "", { shouldValidate: true, shouldDirty: true });
    }
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const [areasResponse, providersResponse] = await Promise.all([
          fetchAccountingAreas(id),
          fetchProviders(1, 100, { condominiumId: id }),
        ]);
        if (isMounted) {
          setAccountingAreas(areasResponse?.data || []);
          setProviders(providersResponse?.data || []);
          setListsLoaded(true);
        }
      } catch (error) {
        handleErrorMessage(error);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!common) return;

    const rawDate = common.expired_at || common.created_at || common.payment_date;
    const initialMom = rawDate && moment(rawDate).isValid() ? moment(rawDate) : moment();

    setSelectedDate(initialMom);

    reset({
      name: common.name || "",
      description: common.description || "",
      accounting_area_id: common.sub_expenses?.[0]?.accounting_area?.id || "",
      provider_id: common.provider?.id || "",
      expired_at: initialMom.format("YYYY-MM-DD HH:mm:ss"),
    });

    const initialAmounts = {};
    (common.properties || []).forEach((item) => {
      if (item?.property?.id) {
        initialAmounts[item.property.id] = item.amount;
      }
    });
    setAmountsByProperty(initialAmounts);
  }, [common, reset]);

  useEffect(() => {
    if (!common || !listsLoaded) return;

    setValue("provider_id", String(common.provider?.id || ""));
    setValue("accounting_area_id", String(common.sub_expenses?.[0]?.accounting_area?.id || ""));
  }, [common, listsLoaded, setValue]);

  const providerOptions =
    common?.provider && !providers.some((provider) => provider.id === common.provider.id)
      ? [...providers, common.provider]
      : providers;

  const handleAmountChange = (propertyId, value) => {
    setAmountsByProperty((prev) => ({
      ...prev,
      [propertyId]: value === "" ? "" : Number(value),
    }));
  };

  const totalAmount =
    Math.round(
      Object.values(amountsByProperty).reduce(
        (acc, value) => acc + (Number(value) || 0),
        0
      ) * 1e6
    ) / 1e6;

  const handleTotalChange = (value) => {
    setTotalDraft(value);
    if (value === "") return;

    setAmountsByProperty((prev) => {
      const propertyIds = Object.keys(prev);
      const shares = splitByWeight(
        Number(value),
        propertyIds.map((propertyId) => Number(prev[propertyId]) || 0)
      );

      return propertyIds.reduce(
        (next, propertyId, index) => ({ ...next, [propertyId]: shares[index] }),
        {}
      );
    });
  };

  const onSubmit = async (values) => {
    const properties = Object.entries(amountsByProperty).map(
      ([propertyId, amount]) => ({
        property_id: Number(propertyId),
        amount: formatAmountForBackend(Number(amount) || 0),
      })
    );

    const paymentError = expensePaymentError(payment, totalAmount);
    if (paymentError) {
      Toast.fire({ type: "error", title: paymentError });
      return;
    }

    await handleSubmitData({
      ...values,
      ...expensePaymentPayload(payment, totalAmount),
      is_provider_common: true,
      value: formatAmountForBackend(totalAmount),
      properties,
    });
  };

  const goBack = () => {
    if (onCancel) {
      onCancel();
      return;
    }

    history.push({
      pathname: `/dashboard/${moduleBase}/${id}/individual-costs`,
      state: {
        type_id: state?.type_id,
        type_name: state?.type_name,
      },
    });
  };

  return (
    <Card className="bg-light mb-3" data-testid="form" tag={Form} onSubmit={handleSubmit(onSubmit)}>
      <PreloadState loading={isSubmitting} message={`Guardando ${state?.type_name || "gasto particular"}...`}>
        <FalconCardHeader title="Editar gasto particular" light={true} />
        <CardBody>
          <FormGroup>
            <Label for="provider_id">
              Proveedor <span className="text-danger">*</span>
            </Label>
            <Input
              id="provider_id"
              name="provider_id"
              type="select"
              innerRef={register({
                required: "Debes seleccionar un proveedor.",
              })}
              className={classNames({ "border-danger": errors.provider_id })}
            >
              <option value="">Selecciona un proveedor</option>
              {providerOptions.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </Input>
            {errors.provider_id && (
              <WizardError
                error={errors.provider_id.message}
                className="fs--1 font-weight-normal d-block"
              />
            )}
          </FormGroup>
          <FormGroup>
            <Label for="name">Nombre</Label>
            <Input
              autoComplete="off"
              id="name"
              name="name"
              innerRef={register({
                required: "Nombre del gasto es requerido.",
              })}
              placeholder="Ej: Reparación de tubería interna"
              className={classNames({ "border-danger": errors.name?.message })}
            />
            {errors.name && (
              <WizardError error={errors.name.message} className="fs--1 font-weight-normal d-block" />
            )}
          </FormGroup>

          <FormGroup>
            <Label for="description">Descripción</Label>
            <Input
              autoComplete="off"
              name="description"
              innerRef={register({
                minLength: {
                  value: 4,
                  message: "La descripción debe tener al menos 4 carácteres.",
                },
              })}
              type="textarea"
              className={classNames("form-control", { "border-danger": errors.description?.message })}
              placeholder="Detalles adicionales..."
            />
            <WizardError error={errors.description?.message} className="fs--1 font-weight-normal d-block" />
          </FormGroup>

          <Row form>
            <Col md={6}>
              <FormGroup>
                <Label for="accounting_area_id">Cuenta / Área Contable</Label>
                <Input
                  type="select"
                  name="accounting_area_id"
                  className={classNames({ "border-danger": errors.accounting_area_id })}
                  innerRef={register({
                    required: "Debes seleccionar un área contable.",
                  })}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Selecciona una Cuenta / Área
                  </option>
                  {expenseAccountingAreas.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
                </Input>
                {errors.accounting_area_id && (
                  <WizardError error={errors.accounting_area_id.message} className="fs--1 font-weight-normal d-block" />
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="expired_at">Fecha del gasto <span className="text-danger">*</span></Label>
                <Datetime
                  value={selectedDate}
                  className={classNames("w-100", { "border-danger": errors.expired_at?.message })}
                  name="expired_at"
                  innerRef={register("expired_at", {
                    required: "La fecha del gasto es obligatoria.",
                  })}
                  onChange={handleDateChange}
                  dateFormat="DD/MM/YYYY"
                  timeFormat={false}
                />
                <WizardError error={errors.expired_at?.message} className="fs--1 font-weight-normal d-block" />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="bg-white border rounded p-3 mt-2">
            {hasSeveralOwners && (
              <FormGroup>
                <Label for="particular_total" className="font-weight-bold">
                  Monto total
                </Label>
                <NumberFormat
                  id="particular_total"
                  customInput={Input}
                  disabled={amountLocked}
                  thousandSeparator
                  prefix={`${baseCurrency.symbol} `}
                  fixedDecimalScale
                  decimalScale={2}
                  placeholder="0.00"
                  value={totalDraft ?? totalAmount}
                  onValueChange={(values, sourceInfo) => {
                    if (sourceInfo?.source === "prop") return;
                    handleTotalChange(values.value);
                  }}
                  onBlur={() => setTotalDraft(null)}
                  className="text-right font-weight-bold"
                />
                <small className="text-600">
                  Al cambiarlo, los montos de cada propietario se recalculan en
                  la misma proporción que tienen. También puedes ajustar cada
                  monto abajo.
                </small>
              </FormGroup>
            )}
            <Label className="font-weight-bold">
              {hasSeveralOwners ? "Montos por propietario" : "Monto del gasto"}
            </Label>
            {(common?.properties || []).map((item) => (
              <Row key={item.property?.id} className="align-items-center mb-2 mx-0 py-2 border-bottom border-100">
                <Col xs={7} className="pl-0">
                  <div className="font-weight-semi-bold text-nowrap text-truncate">
                    {item.property?.user?.username}
                  </div>
                  <div className="fs--2 text-600 text-truncate">
                    {item.property?.user?.data?.firstname}{" "}
                    {item.property?.user?.data?.lastname}
                  </div>
                </Col>
                <Col xs={5} className="pr-0">
                  <NumberFormat
                    customInput={Input}
                    disabled={amountLocked}
                    bsSize="sm"
                    thousandSeparator
                    prefix={`${baseCurrency.symbol} `}
                    fixedDecimalScale
                    decimalScale={2}
                    placeholder="0.00"
                    value={amountsByProperty[item.property?.id] ?? ""}
                    onValueChange={(values, sourceInfo) => {
                      if (sourceInfo?.source === "prop") return;
                      handleAmountChange(item.property?.id, values.value);
                    }}
                    className="text-right font-weight-bold"
                  />
                </Col>
              </Row>
            ))}
            <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
              <span className="font-weight-bold">Total:</span>
              <span className="font-weight-bold fs-1">
                {`${baseCurrency.symbol} ${totalAmount.toFixed(2)}`}
              </span>
            </div>
          </FormGroup>

          {common?.next_charge && (common.next_charge.paid || amountLocked) && (
            <ExpensePaidNotice
              title={
                common.next_charge.paid
                  ? "El próximo cobro de este gasto ya está pagado."
                  : "El monto de este gasto está bloqueado."
              }
              detail={
                !amountLocked
                  ? null
                  : common.next_charge.paid
                  ? "Para cambiar el monto, anula el pago en Pagos a proveedores."
                  : common.next_charge.amount_lock_reason
              }
            />
          )}

          {common?.next_charge && !common.next_charge.paid && (
            <ExpensePaymentFields
              condominiumId={id}
              amount={totalAmount}
              value={payment}
              onChange={setPayment}
              helpText="Si ya le pagaste al proveedor el próximo cobro de este gasto, registra el pago aquí: cuando se emita la planilla, su cuenta por pagar ya va a estar pagada."
            />
          )}

          <Row form className="align-items-center justify-content-center justify-content-md-end mt-4">
            <ButtonIcon
              color="falcon-default"
              className="my-2 mx-0 mx-md-2 m-md-0 px-5 mr-2"
              icon={faUndo}
              iconAlign="right"
              transform="down-1 shrink-4"
              onClick={goBack}
              type="button"
            >
              Volver
            </ButtonIcon>
            <ButtonIcon
              data-testid="submit-button"
              color="primary"
              className="my-2 mx-0 mx-md-2 m-md-0 px-5"
              type="submit"
              icon={faSave}
              iconAlign="right"
              disabled={isSubmitting}
              transform="down-1 shrink-4"
            >
              Guardar
            </ButtonIcon>
          </Row>
        </CardBody>
      </PreloadState>
    </Card>
  );
};

export default withRedirect(CreateIndividualCostsEditForm);
